import { useTranslation } from '@kgc/i18n';
import { Badge } from '@kgc/ui';
import { AlertTriangle, Bug, ChevronDown, ChevronRight, Clock, Sparkles, Star } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChangelogEntry } from '../data/changelog-data';
import { releaseTests } from '../data/release-tests-data';
import { versionToHash } from '../utils/version-hash';
import { VersionBadge } from './VersionBadge';

const typeIcons = {
  feature: Sparkles,
  fix: Bug,
  improvement: Star,
  breaking: AlertTriangle,
} as const;

const typeColors = {
  feature: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
  fix: 'bg-red-500/10 text-red-500 border-red-500/30',
  improvement: 'bg-green-500/10 text-green-500 border-green-500/30',
  breaking: 'bg-orange-500/10 text-orange-500 border-orange-500/30',
} as const;

interface ChangelogTabProps {
  filteredData: ChangelogEntry[];
  onNavigateToTesting: (version: string) => void;
  scrollToVersion?: string | null;
  noResults?: boolean;
  onClearFilters?: () => void;
}

export function ChangelogTab({
  filteredData,
  onNavigateToTesting,
  scrollToVersion,
  noResults,
  onClearFilters,
}: ChangelogTabProps) {
  const { t, locale } = useTranslation();
  const langKey = locale === 'hu' ? 'Hu' : locale === 'de' ? 'De' : locale === 'uk' ? 'Uk' : 'En';
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(filteredData[0] ? [filteredData[0].version] : [])
  );
  const versionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const toggleVersion = useCallback((version: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(version)) next.delete(version);
      else next.add(version);
      return next;
    });
  }, []);

  // Auto-scroll when deep link target changes
  useEffect(() => {
    if (scrollToVersion) {
      setExpanded(prev => new Set([...prev, scrollToVersion]));
      setTimeout(() => {
        versionRefs.current[scrollToVersion]?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 100);
    }
  }, [scrollToVersion]);

  const hasTestSteps = (version: string) => releaseTests.some(rt => rt.version === version);

  return (
    <div className="space-y-3">
      {noResults && (
        <div className="py-8 text-center">
          <p className="text-muted-foreground">{t('releaseHub.filters.noResults')}</p>
          {onClearFilters && (
            <button onClick={onClearFilters} className="mt-2 text-sm text-primary hover:underline">
              {t('releaseHub.filters.clearFilters')}
            </button>
          )}
        </div>
      )}

      {!noResults && filteredData.length === 0 && (
        <p className="py-8 text-center text-muted-foreground">
          {t('releaseHub.changelog.noEntries')}
        </p>
      )}

      {filteredData.map(entry => (
        <VersionAccordion
          key={entry.version}
          entry={entry}
          lang={langKey}
          isExpanded={expanded.has(entry.version)}
          onToggle={() => toggleVersion(entry.version)}
          hasTestSteps={hasTestSteps(entry.version)}
          onNavigateToTesting={onNavigateToTesting}
          ref={el => {
            versionRefs.current[entry.version] = el;
          }}
        />
      ))}
    </div>
  );
}

interface VersionAccordionProps {
  entry: ChangelogEntry;
  lang: 'Hu' | 'En' | 'De' | 'Uk';
  isExpanded: boolean;
  onToggle: () => void;
  hasTestSteps: boolean;
  onNavigateToTesting: (version: string) => void;
}

import { forwardRef } from 'react';

const VersionAccordion = forwardRef<HTMLDivElement, VersionAccordionProps>(
  function VersionAccordion(
    { entry, lang, isExpanded, onToggle, hasTestSteps, onNavigateToTesting },
    ref
  ) {
    const { t } = useTranslation();
    return (
      <div
        ref={ref}
        className="rounded-lg border border-border bg-card"
        id={`changelog-v${versionToHash(entry.version)}`}
      >
        {/* Header */}
        <button
          onClick={onToggle}
          className="flex w-full items-center justify-between p-4 text-left hover:bg-accent/50"
        >
          <div className="flex items-center gap-3">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
            <span className="font-semibold">v{entry.version}</span>
            {hasTestSteps && (
              <VersionBadge
                label={t('releaseHub.changelog.testStepsBadge')}
                version={entry.version}
                onClick={onNavigateToTesting}
              />
            )}
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            {entry.date}
            <span className="ml-2">
              {entry.changes.length} {t('releaseHub.changelog.changes')}
            </span>
          </div>
        </button>

        {/* Content */}
        {isExpanded && (
          <div className="border-t border-border px-4 pb-4 pt-2">
            <div className="space-y-2">
              {entry.changes.map((change, idx) => {
                const Icon = typeIcons[change.type];
                const title = change[`title${lang}`] ?? change.titleEn;
                const desc = change[`description${lang}`] ?? change.descriptionEn;

                return (
                  <div key={idx} className="flex items-start gap-3 py-1.5">
                    <Badge variant="outline" className={`mt-0.5 gap-1 ${typeColors[change.type]}`}>
                      <Icon className="h-3 w-3" />
                      {t(`releaseHub.changelog.types.${change.type}`)}
                    </Badge>
                    <Badge variant="secondary" className="mt-0.5 text-xs">
                      {change.category}
                    </Badge>
                    <div className="flex-1">
                      <p className="text-sm font-medium">{title}</p>
                      {desc && <p className="mt-0.5 text-xs text-muted-foreground">{desc}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }
);
